// 2025-08-19: FILE UPDATED — sanitize Firestore writes to strip undefined/NaN values and avoid offline update errors

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  collection,
  // addDoc, // 2025-08-19: REMOVED — switch to doc+setDoc for optimistic offline writes
  updateDoc,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  getDocsFromCache, // ADDED — used for timeout fallback to cache
  query,
  where,
  onSnapshot,
  setDoc, // 2025-08-19: ADDED — used for optimistic write (non-blocking offline)
  deleteField, // 2025-08-19: ADDED — used by emptyToDelete helper
  writeBatch,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from './AuthContext';
import toast from 'react-hot-toast';
import { PublicCruiseView } from '../types/friends';
import { debug } from '../utils/debug';
import { logEvent } from '../services/analyticsService';

// 2025-08-19: ADDED — utils to sanitize payloads before sending to Firestore
// --- utils: keep 0/false, remove undefined/NaN, recurse objects ---
function stripForFirestore<T extends Record<string, any>>(obj: T): Partial<T> {
  const out: Record<string, any> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === undefined) continue;
    if (typeof v === 'number' && Number.isNaN(v)) continue;

    if (v && typeof v === 'object' && !Array.isArray(v) && !(v instanceof Date)) {
      const nested = stripForFirestore(v as Record<string, any>);
      if (Object.keys(nested).length) out[k] = nested;
    } else {
      out[k] = v;
    }
  }
  return out as Partial<T>;
}

import { removeUndefined } from '../utils/formatUtils';

// 2025-08-19: ADDED — helper to convert blank values to deleteField()
export const emptyToDelete = (v: unknown) => (v === '' || v === null ? deleteField() : v);

// Helper function to sanitize cruise data for friend viewing
export const sanitizeCruiseForFriend = (cruise: Cruise): PublicCruiseView => {
  const sanitized: PublicCruiseView = {
    id: cruise.id,
    cruiseLine: cruise.cruiseLine,
    shipName: cruise.shipName,
    departureDate: cruise.departureDate,
    returnDate: cruise.returnDate,
    departurePort: cruise.departurePort,
    ports: (cruise.ports || []).map(port => ({
      id: port.id,
      name: port.name,
      date: port.date,
      arrivalTime: port.arrivalTime,
      departureTime: port.departureTime,
    })),
    travelDetails: cruise.travelDetails?.map(td => ({
      id: td.id,
      type: td.type,
      departureLocation: td.departureLocation,
      arrivalLocation: td.arrivalLocation,
      departureTime: td.departureTime,
      arrivalTime: td.arrivalTime,
    })),
    diningTime: cruise.diningTime,
    hasDrinkPackage: cruise.hasDrinkPackage,
    hasInternetPackage: cruise.hasInternetPackage,
    imageUrl: cruise.imageUrl,
    created: cruise.created,
    userId: cruise.userId,
    completed: cruise.completed,
    ratings: cruise.ratings,
  };

  return removeUndefined(sanitized);
};

export interface Port {
  id: string;
  name: string;
  date: string;
  arrivalTime?: string;
  departureTime?: string;
  excursion?: string;
  fromItinerary?: boolean; // true when port data originates from an official itinerary
}

export interface TravelDetail {
  id: string;
  type: 'rental-car' | 'train' | 'airplane';
  company?: string;
  reservationNumber?: string;
  departureTime?: string;
  arrivalTime?: string;
  departureLocation?: string;
  arrivalLocation?: string;
  flightNumber?: string;
  trainNumber?: string;
  carType?: string;
  pickupLocation?: string;
  returnLocation?: string;
  pickupTime?: string;
  returnTime?: string;
}

export interface Cruise {
  id: string;
  cruiseLine: string;
  shipName: string;
  departureDate: string;
  returnDate: string;
  departurePort: string;
  cabinNumber?: string | null;
  cabinType: string;
  isGuaranteedCabin: boolean;
  bookingNumber?: string | null;
  cruiseCost?: number;
  portTaxesFees?: number;
  additionalCosts?: number;
  isComplimentaryCasinoSailing?: boolean;
  notes?: string;
  ports: Port[];
  travelDetails?: TravelDetail[];
  diningTime?: string;
  hasDrinkPackage?: boolean;
  hasInternetPackage?: boolean;
  imageUrl?: string;
  created: number;
  userId: string;
  // New post-cruise fields
  completed?: boolean;
  gamblingWins?: number;
  gamblingLosses?: number;
  onboardExpenses?: number;
  loyaltyPointsOverride?: number; // 2025-08-19: ADDED — align types with UI field to avoid undefined write mistakes
  loyaltyAccrualCruiseLine?: string; // Captures if user designated points to an alternate group program
  // Travel agent information
  travelAgentName?: string;
  travelAgentPhone?: string;
  travelAgentEmail?: string;
  // Cruise ratings (1-10 scale)
  ratings?: {
    shipExperience?: number; // Ship amenities, cleanliness, venues
    service?: number; // Staff service
    stateroom?: number; // Cabin quality
    foodDining?: number; // Buffet, main dining, specialty restaurants
    barBeverages?: number; // Bar service and beverages
    entertainment?: number; // Shows, music, activities
  };
  // Itinerary sync
  itineraryKey?: string | null;       // Composite doc ID: "CruiseLine_ShipName_sailingDate_returnDate"
  itineraryTitle?: string | null;     // Marketing title e.g. "7 Night Western Caribbean & Perfect Day"
  itinerarySyncedAt?: number;  // ms timestamp of last sync (set on add + each auto-sync)
  linkBroken?: boolean;
  previousItineraryKey?: string;
  unlinkedAt?: number;
  invitedFriendIds?: string[];
}

interface CruiseContextType {
  cruises: Cruise[];
  loading: boolean;
  addCruise: (cruise: Omit<Cruise, 'id' | 'created' | 'userId'>) => Promise<string>;
  updateCruise: (id: string, cruise: Partial<Cruise>, options?: { silent?: boolean }) => Promise<void>;
  deleteCruise: (id: string) => Promise<void>;
  getCruise: (id: string) => Promise<Cruise | null>;
  getAllCruisesForExport: () => Promise<Cruise[]>;
  bulkAddCruises: (cruisesData: Omit<Cruise, 'id' | 'created' | 'userId'>[]) => Promise<void>;
  getFriendCruise: (cruiseId: string, friendId: string) => Promise<PublicCruiseView | null>;
  canFriendViewCruise: (cruiseId: string, friendId: string, friendAccessLevel: import('../types/friends').FriendAccessLevel) => Promise<boolean>;
}

const CruiseContext = createContext<CruiseContextType | null>(null);

export const useCruises = () => {
  const context = useContext(CruiseContext);
  if (!context) {
    throw new Error('useCruises must be used within a CruiseProvider');
  }
  return context;
};

interface CruiseProviderProps {
  children: ReactNode;
}

export const CruiseProvider = ({ children }: CruiseProviderProps) => {
  const [cruises, setCruises] = useState<Cruise[]>([]);
  const [loading, setLoading] = useState(true);
  const { currentUser } = useAuth();

  useEffect(() => {
    if (!currentUser || !currentUser.emailVerified) {
      setCruises([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const cruisesRef = collection(db, 'cruises');
    const q = query(cruisesRef, where('userId', '==', currentUser.uid));

    let timeoutId: NodeJS.Timeout | null = null;
    let snapshotUnsub: (() => void) | null = null;
    let hasReceivedData = false;

    // Phase 1: Try cache immediately (fastest path - ~50-200ms)
    const tryImmediateCache = async () => {
      try {
        const cachedSnapshot = await getDocsFromCache(q);
        if (cachedSnapshot.docs.length > 0) {
          const cruisesData = cachedSnapshot.docs.map((d) => ({ id: d.id, ...d.data() })) as Cruise[];
          setCruises(cruisesData);
          setLoading(false);
          hasReceivedData = true;
          debug.debug('[cruises] Loaded from cache immediately (~50-200ms)');
          return true;
        }
      } catch {
        // No cache available yet - that's okay
        debug.debug('[cruises] No cache available for immediate load');
      }
      return false;
    };

    // Phase 2: Timeout fallback (800ms) - only if immediate cache didn't work
    const setupTimeoutFallback = () => {
      timeoutId = setTimeout(async () => {
        if (!hasReceivedData) {
          debug.debug('[cruises] Timeout reached (800ms), trying cache fallback');
          try {
            const cachedSnapshot = await getDocsFromCache(q);
            if (cachedSnapshot.docs.length > 0) {
              const cruisesData = cachedSnapshot.docs.map((d) => ({ id: d.id, ...d.data() })) as Cruise[];
              setCruises(cruisesData);
              setLoading(false);
              debug.debug('[cruises] Loaded from cache (timeout fallback)');
            }
          } catch {
            // Still no cache - will wait for onSnapshot
            debug.debug('[cruises] No cache available for timeout fallback');
          }
        }
      }, 800); // 800ms timeout - optimal balance between speed and reliability
    };

    // Initialize: try cache immediately, then set up timeout if needed
    tryImmediateCache()
      .then((cacheFound) => {
        if (!cacheFound) {
          setupTimeoutFallback();
        }
      })
      .catch(() => {
        setupTimeoutFallback();
      });

    // Phase 3: Always set up snapshot listener for updates and background sync
    // This stays active and will update UI when server responds (even if slow)
    snapshotUnsub = onSnapshot(
      q,
      { includeMetadataChanges: true },
      (snapshot) => {
        hasReceivedData = true;
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }

        const cruisesData = snapshot.docs.map((d) => ({ id: d.id, ...d.data() })) as Cruise[];
        setCruises(cruisesData);
        // 2025-08-19: ADDED — low-noise debug to distinguish cache/server + pending writes
        debug.debug(
          `[cruises] ${snapshot.metadata.fromCache ? 'cache' : 'server'} update; pendingWrites=${snapshot.metadata.hasPendingWrites}`
        );
        // If data is from cache and we're online, subsequent event will arrive from server
        setLoading(false);
      },
      (err) => {
        console.error('Error listening to cruises:', err);
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        setLoading(false);
      }
    );

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (snapshotUnsub) snapshotUnsub();
    };
  }, [currentUser]);

  const addCruise = async (cruise: Omit<Cruise, 'id' | 'created' | 'userId'>) => {
    if (!currentUser) throw new Error('You must be logged in');

    try {
      const cruiseData = {
        ...cruise,
        created: Date.now(),
        userId: currentUser.uid,
      };

      // 2025-08-19: ADDED — sanitize initial payload to avoid undefined/NaN writes
      const cleaned = stripForFirestore(cruiseData);

      // const docRef = await addDoc(collection(db, 'cruises'), cruiseData);
      // 2025-08-19: REMOVED — awaiting addDoc can hang offline in WebView. Switch to optimistic doc+setDoc.

      // 2025-08-19: ADDED — create ID up front so we can update UI immediately
      const ref = doc(collection(db, 'cruises'));

      // 2025-08-19: CHANGED — optimistic local update uses cleaned values
      const optimistic: Cruise = { ...(cleaned as Cruise), id: ref.id };

      setCruises((prev) => [...prev, optimistic]);

      // 2025-08-19: ADDED — queue write locally; DO NOT await (avoids spinner hang offline)
      setDoc(ref, cleaned)
        .then(() => {
          debug.debug('[addCruise] queued/persisted locally; will sync when online', { id: ref.id });
        })
        .catch((error) => {
          console.error('[addCruise] failed to persist locally', error);
          // 2025-08-19: ADDED — optional rollback of optimistic item if local persistence fails
          setCruises((prev) => prev.filter((c) => c.id !== ref.id));
          toast.error('Failed to queue save locally'); // user feedback
        });

      // 2025-08-19: UPDATED — friendlier message when offline
      toast.success(navigator.onLine ? 'Cruise added successfully!' : 'Saved offline — will sync when online');

      // Track the add_cruise event
      logEvent('add_cruise', { cruiseLine: cruise.cruiseLine, shipName: cruise.shipName });

      // 2025-08-19: UPDATED — return immediately so the form can stop spinner / navigate
      return ref.id;

      // setCruises(prev => [...prev, { ...cruiseData, id: docRef.id } as Cruise]);
      // toast.success('Cruise added successfully!');
      // return docRef.id;
      // 2025-08-19: REMOVED — replaced by optimistic flow above
    } catch (error) {
      console.error('Error adding cruise:', error);
      toast.error('Failed to add cruise');
      throw error;
    }
  };

  const updateCruise = async (id: string, cruiseUpdates: Partial<Cruise>, options?: { silent?: boolean }) => {
    if (!currentUser) throw new Error('You must be logged in');

    try {
      const cruiseRef = doc(db, 'cruises', id);

      // 2025-08-19: ADDED — sanitize updates to strip undefined/NaN before sending to Firestore
      const cleaned = stripForFirestore(cruiseUpdates);

      // 2025-08-19: ADDED — if nothing valid to write, exit gracefully
      if (Object.keys(cleaned).length === 0) {
        if (!options?.silent) toast.success('Cruise updated'); // nothing to write, but don’t error
        return;
      }

      // 2025-08-19: CHANGED — optimistic local update first (immediate UI response)
      setCruises((prev) => prev.map((cruise) => (cruise.id === id ? { ...cruise, ...cleaned } : cruise)));

      // 2025-08-19: CHANGED — queue write locally; DO NOT await (avoids spinner hang offline)
      updateDoc(cruiseRef, cleaned)
        .then(() => {
          debug.debug('[updateCruise] queued/persisted locally; will sync when online', { id });
        })
        .catch((error) => {
          console.error('[updateCruise] failed to persist locally', error);
          // 2025-08-19: ADDED — optional rollback of optimistic update if local persistence fails
          // Note: In practice, this is rare since we're using Firestore's offline cache
          toast.error('Failed to queue update locally');
        });

      // 2025-08-19: UPDATED — friendlier message when offline (suppressed if options.silent is true)
      if (!options?.silent) {
        toast.success(navigator.onLine ? 'Cruise updated successfully!' : 'Updated offline — will sync when online');
      }

      // Track edit_cruise
      logEvent('edit_cruise', { cruiseId: id });
    } catch (error: any) {
      console.error('Error updating cruise:', error);
      if (error.code === 'permission-denied') {
        toast.error('You do not have permission to update this cruise');
      } else {
        toast.error('Failed to update cruise');
      }
      throw error;
    }
  };

  const deleteCruise = async (id: string) => {
    if (!currentUser) throw new Error('You must be logged in');

    try {
      const cruiseRef = doc(db, 'cruises', id);
      const cruiseSnap = await getDoc(cruiseRef);

      if (!cruiseSnap.exists()) {
        throw new Error('Cruise not found');
      }

      const cruiseData = cruiseSnap.data();
      if (cruiseData.userId !== currentUser.uid) {
        throw new Error('permission-denied');
      }

      await deleteDoc(cruiseRef);
      setCruises((prev) => prev.filter((cruise) => cruise.id !== id));
      toast.success('Cruise deleted successfully');

      // Track delete_cruise
      logEvent('delete_cruise', { cruiseId: id });
    } catch (error: any) {
      console.error('Error deleting cruise:', error);
      if (error.message === 'permission-denied' || error.code === 'permission-denied') {
        toast.error('You do not have permission to delete this cruise');
      } else if (error.message === 'Cruise not found') {
        toast.error('Cruise not found');
      } else {
        toast.error('Failed to delete cruise');
      }
      throw error;
    }
  };

  const getCruise = async (id: string): Promise<Cruise | null> => {
    if (!currentUser) throw new Error('You must be logged in');

    try {
      const cruiseRef = doc(db, 'cruises', id);
      const cruiseSnap = await getDoc(cruiseRef);

      if (cruiseSnap.exists()) {
        const cruiseData = cruiseSnap.data();
        if (cruiseData.userId !== currentUser.uid) {
          toast.error('You do not have permission to view this cruise');
          return null;
        }
        return { id: cruiseSnap.id, ...cruiseData } as Cruise;
      } else {
        toast.error('Cruise not found');
        return null;
      }
    } catch (error: any) {
      console.error('Error getting cruise:', error);
      if (error.code === 'permission-denied') {
        toast.error('You do not have permission to view this cruise');
      } else {
        toast.error('Failed to retrieve cruise details');
      }
      throw error;
    }
  };

  const canFriendViewCruise = async (
    cruiseId: string,
    friendId: string,
    friendAccessLevel: import('../types/friends').FriendAccessLevel
  ): Promise<boolean> => {
    if (!currentUser) return false;

    try {
      const cruiseRef = doc(db, 'cruises', cruiseId);
      const cruiseSnap = await getDoc(cruiseRef);

      if (!cruiseSnap.exists()) return false;
      const cruiseData = cruiseSnap.data();

      // Check if this cruise belongs to the friend
      if (cruiseData.userId !== friendId) return false;

      // For full-access friends, they can see all cruises
      if (friendAccessLevel === 'full-access') return true;

      // For restricted-access friends, check if cruise is shared
      const sharedCruiseRef = doc(db, 'sharedCruises', friendId, 'cruises', cruiseId);
      const sharedCruiseSnap = await getDoc(sharedCruiseRef);
      return sharedCruiseSnap.exists();
    } catch (error) {
      console.error('Error checking friend cruise access:', error);
      return false;
    }
  };

  const getFriendCruise = async (
    cruiseId: string,
    friendId: string
  ): Promise<PublicCruiseView | null> => {
    if (!currentUser) throw new Error('You must be logged in');

    try {
      // First check if we're friends and get access level
      const friendshipRef = doc(db, 'friendships', currentUser.uid, 'friends', friendId);
      const friendshipSnap = await getDoc(friendshipRef);

      if (!friendshipSnap.exists()) {
        toast.error('You are not friends with this user');
        return null;
      }

      const friendship = friendshipSnap.data();
      const accessLevel = friendship.accessLevel as import('../types/friends').FriendAccessLevel;

      // Check if friend can view this cruise
      const canView = await canFriendViewCruise(cruiseId, friendId, accessLevel);
      if (!canView) {
        toast.error('You do not have permission to view this cruise');
        return null;
      }

      // Get the cruise
      const cruiseRef = doc(db, 'cruises', cruiseId);
      const cruiseSnap = await getDoc(cruiseRef);

      if (!cruiseSnap.exists()) {
        toast.error('Cruise not found');
        return null;
      }

      const cruiseData = cruiseSnap.data();
      if (cruiseData.userId !== friendId) {
        toast.error('This cruise does not belong to the specified friend');
        return null;
      }

      const cruise = { id: cruiseSnap.id, ...cruiseData } as Cruise;
      return sanitizeCruiseForFriend(cruise);
    } catch (error: any) {
      console.error('Error getting friend cruise:', error);
      if (error.code === 'permission-denied') {
        toast.error('You do not have permission to view this cruise');
      } else {
        toast.error('Failed to retrieve cruise details');
      }
      throw error;
    }
  };

  // Export method - gets all cruises for the current user
  const getAllCruisesForExport = async (): Promise<Cruise[]> => {
    if (!currentUser) {
      throw new Error('User must be authenticated');
    }
    
    const cruisesRef = collection(db, 'cruises');
    const q = query(cruisesRef, where('userId', '==', currentUser.uid));
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data()
    } as Cruise));
  };

  // Bulk import method - adds multiple cruises at once
  const bulkAddCruises = async (cruisesData: Omit<Cruise, 'id' | 'created' | 'userId'>[]): Promise<void> => {
    if (!currentUser) {
      throw new Error('User must be authenticated');
    }
    
    const batch = writeBatch(db);
    
    cruisesData.forEach(cruiseData => {
      const cruiseRef = doc(collection(db, 'cruises'));
      const sanitizedData = stripForFirestore({
        ...cruiseData,
        userId: currentUser.uid,
        created: Date.now()
      });
      batch.set(cruiseRef, sanitizedData);
    });
    
    await batch.commit();
    
    toast.success(`Successfully imported ${cruisesData.length} cruise${cruisesData.length !== 1 ? 's' : ''}!`);
  };

  const value = {
    cruises,
    loading,
    addCruise,
    updateCruise,
    deleteCruise,
    getCruise,
    getAllCruisesForExport,
    bulkAddCruises,
    getFriendCruise,
    canFriendViewCruise,
  };

  return <CruiseContext.Provider value={value}>{children}</CruiseContext.Provider>;
};
